import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../config";

const apiUrl = config.apiUrl;

const ViewTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [services, setServices] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editFormData, setEditFormData] = useState({
    item_id: "",
    item_type: "",
    name: "",
    mobile: "",
    transaction_id: "",
    amount: "",
    referred_by: "",
  });

  // Filter states
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Date format function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Fetch transactions, services, and memberships from APIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [responseTnx, responseService, responseMembership] = await Promise.all([
          axios.get(`${apiUrl}/transactions`),
          axios.get(`${apiUrl}/services`),
          axios.get(`${apiUrl}/memberships`),
        ]);

        const transactionsWithDate = responseTnx.data.transactions.map(transaction => ({
          ...transaction,
          createdDate: transaction.created_at ? formatDate(transaction.created_at).split(' ')[0] : formatDate(new Date()).split(' ')[0],
          createdDateTime: transaction.created_at ? new Date(transaction.created_at) : new Date(),
          formattedDate: transaction.created_at ? formatDate(transaction.created_at) : formatDate(new Date())
        }));

        setTransactions(transactionsWithDate);
        setFilteredTransactions(transactionsWithDate);
        setServices(responseService.data.services);
        setMemberships(responseMembership.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply date filter
  useEffect(() => {
    let filtered = transactions;

    if (dateFilter) {
      const filterDate = formatDate(dateFilter).split(' ')[0];
      filtered = filtered.filter(transaction => transaction.createdDate === filterDate);
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter(transaction => {
        const transactionDate = transaction.createdDateTime;
        return transactionDate >= start && transactionDate <= end;
      });
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [dateFilter, startDate, endDate, transactions]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Helper function to find the item name based on item_type and item_id
  const findItemName = (itemType, itemId) => {
    if (itemType === "service") {
      const service = services?.find((service) => service.id === itemId);
      return service ? service.service_name : "Service Not Found";
    } else if (itemType === "membership") {
      const membership = memberships?.find((membership) => membership.id === itemId);
      return membership ? membership.membership_type : "Membership Not Found";
    }
    return "Unknown Item";
  };

  // Get last 3 days transaction counts
  const getLast3DaysTransactionCounts = () => {
    const counts = {};
    const today = new Date();
    
    // Create an array of last 3 dates including today
    const last3Days = [];
    for (let i = 0; i < 3; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      last3Days.push(formatDate(date).split(' ')[0]);
    }

    // Count transactions for each of the last 3 days
    last3Days.forEach(date => {
      counts[date] = transactions.filter(transaction => transaction.createdDate === date).length;
    });

    return counts;
  };

  const last3DaysCounts = getLast3DaysTransactionCounts();

  // Reset filters
  const resetFilters = () => {
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        await axios.delete(`${apiUrl}/transactions/${id}`);
        setTransactions((prev) => prev.filter((transaction) => transaction.id !== id));
        alert("Transaction deleted successfully!");
      } catch (err) {
        console.error("Error deleting transaction:", err);
        setError("An error occurred while deleting the transaction.");
      }
    }
  };

  // Handle Approve
  const handleApprove = async (id, currentApproval) => {
    if (window.confirm("Are you sure you want to approve this transaction?")) {
      try {
        await axios.post(`${apiUrl}/transactions/approve/${id}`);
        setTransactions((prev) =>
          prev.map((transaction) =>
            transaction.id === id ? { ...transaction, approval: !currentApproval } : transaction
          )
        );
        alert("Transaction approved successfully!");
      } catch (err) {
        console.error("Error approving transaction:", err);
        setError("An error occurred while approving the transaction.");
      }
    }
  };

  // Handle Edit Button Click
  const handleEdit = (transaction) => {
    setEditingTransaction(transaction.id);
    setEditFormData({
      item_id: transaction.item_id,
      item_type: transaction.item_type,
      name: transaction.name,
      mobile: transaction.mobile,
      transaction_id: transaction.transaction_id,
      amount: transaction.amount,
      referred_by: transaction.referred_by,
    });
  };

  // Handle Edit Form Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Save Edited Transaction
  const handleSaveEdit = async () => {
    try {
      await axios.put(`${apiUrl}/transactions/${editingTransaction}`, editFormData);
      setTransactions((prev) =>
        prev.map((transaction) =>
          transaction.id === editingTransaction ? { ...transaction, ...editFormData } : transaction
        )
      );
      alert("Transaction updated successfully!");
      setEditingTransaction(null);
    } catch (err) {
      console.error("Error updating transaction:", err);
      setError("An error occurred while updating the transaction.");
    }
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Show limited page numbers (max 5 at a time)
  const getDisplayedPages = () => {
    const maxPages = 5;
    if (totalPages <= maxPages) return pageNumbers;
    
    const half = Math.floor(maxPages / 2);
    let start = currentPage - half;
    let end = currentPage + half;
    
    if (start < 1) {
      start = 1;
      end = maxPages;
    }
    
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxPages + 1);
    }
    
    return pageNumbers.slice(start - 1, end);
  };

  // Function to get today, yesterday, day before in formatted date
  const getDateLabels = () => {
    const today = formatDate(new Date()).split(' ')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dayBefore = new Date();
    dayBefore.setDate(dayBefore.getDate() - 2);
    
    return {
      today: today,
      yesterday: formatDate(yesterday).split(' ')[0],
      dayBefore: formatDate(dayBefore).split(' ')[0]
    };
  };

  const dateLabels = getDateLabels();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4">Transactions List</h2>
      
      {/* Last 3 Days Transaction Counts */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <h3 className="text-lg font-bold mb-3">Last 3 Days Transaction Counts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(last3DaysCounts).map(([date, count]) => {
            let dayLabel = "";
            if (date === dateLabels.today) dayLabel = " (Today)";
            else if (date === dateLabels.yesterday) dayLabel = " (Yesterday)";
            else if (date === dateLabels.dayBefore) dayLabel = " (Day Before)";
            
            return (
              <div key={date} className="bg-white p-4 rounded shadow border">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">
                    {date}
                    <span className="text-blue-600 text-sm ml-1">{dayLabel}</span>
                  </span>
                  <span className={`text-lg font-bold ${
                    count > 0 ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {count}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  {count === 0 ? 'No transactions' : `${count} transaction${count > 1 ? 's' : ''}`}
                </div>
                {count > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        const [day, month, year] = date.split('/');
                        const filterDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        setDateFilter(filterDate);
                        setStartDate("");
                        setEndDate("");
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      View these transactions
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Total transactions summary */}
        <div className="mt-4 p-3 bg-blue-50 rounded">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-blue-800">Total (Last 3 days):</span>
            <span className="font-bold text-blue-800">
              {Object.values(last3DaysCounts).reduce((sum, count) => sum + count, 0)} transactions
            </span>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <h3 className="text-lg font-bold mb-3">Filter Transactions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Single Date Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Date:</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-3">
          <p className="text-sm text-gray-600">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTransactions.length)} of {filteredTransactions.length} transactions
            {dateFilter && ` for ${formatDate(dateFilter).split(' ')[0]}`}
            {startDate && endDate && ` from ${formatDate(startDate).split(' ')[0]} to ${formatDate(endDate).split(' ')[0]}`}
          </p>
        </div>
      </div>

      {error && <p className="text-red-500">{error}</p>}
      
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr>
            <th className="border border-gray-300 px-4 py-2">Item Name</th>
            <th className="border border-gray-300 px-4 py-2">Item Type</th>
            <th className="border border-gray-300 px-4 py-2">Name</th>
            <th className="border border-gray-300 px-4 py-2">Mobile</th>
            <th className="border border-gray-300 px-4 py-2">Transaction ID</th>
            <th className="border border-gray-300 px-4 py-2">Amount</th>
            <th className="border border-gray-300 px-4 py-2">Referred By</th>
            <th className="border border-gray-300 px-4 py-2">Date & Time</th>
            <th className="border border-gray-300 px-4 py-2">Approval</th>
            <th className="border border-gray-300 px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems?.map((transaction) => (
            <tr key={transaction.id}>
              <td className="border border-gray-300 px-4 py-2">
                {findItemName(transaction.item_type, transaction.item_id)}
              </td>
              <td className="border border-gray-300 px-4 py-2">{transaction.item_type || "N/A"}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.name}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.mobile}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.transaction_id}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.amount}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.referred_by || "N/A"}</td>
              <td className="border border-gray-300 px-4 py-2">{transaction.formattedDate}</td>
              <td className="border border-gray-300 px-4 py-2">
                {transaction.approval ? (
                  <span className="text-green-500 font-bold">Approved</span>
                ) : (
                  <button
                    onClick={() => handleApprove(transaction.id, transaction.approval)}
                    className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Approve
                  </button>
                )}
              </td>
              <td className="border border-gray-300 px-4 py-2">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleDelete(transaction.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => handleEdit(transaction)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {currentItems.length === 0 && !loading && (
        <div className="text-center py-4 text-gray-500">
          No transactions found for the selected filter criteria.
        </div>
      )}

      {/* Pagination */}
      {filteredTransactions.length > itemsPerPage && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} • {filteredTransactions.length} total transactions
          </div>
          
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex gap-1">
              {getDisplayedPages().map(number => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`px-3 py-1 rounded ${
                    currentPage === number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {number}
                </button>
              ))}
              
              {/* Ellipsis for more pages */}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className="px-2 py-1">...</span>
              )}
            </div>

            {/* Next Button */}
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Next
            </button>
          </div>

          <div className="text-sm text-gray-600">
            {itemsPerPage} items per page
          </div>
        </div>
      )}

      {/* Edit form for editing a transaction */}
      {editingTransaction && (
        <div className="mt-6 p-4 bg-gray-50 rounded">
          <h3 className="text-xl font-bold mb-4">Edit Transaction</h3>
          <form>
            {/* Item Type Select */}
            <div className="mb-4">
              <label className="block text-gray-700">Item Type:</label>
              <select
                name="item_type"
                value={editFormData.item_type}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              >
                <option value="service">Service</option>
                <option value="membership">Membership</option>
              </select>
            </div>

            {/* Item Name Select (based on item type) */}
            <div className="mb-4">
              <label className="block text-gray-700">Item Name:</label>
              <select
                name="item_id"
                value={editFormData.item_id}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              >
                {editFormData.item_type === "service" &&
                  services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.service_name}
                    </option>
                  ))}
                {editFormData.item_type === "membership" &&
                  memberships.map((membership) => (
                    <option key={membership.id} value={membership.id}>
                      {membership.membership_type}
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700">Name:</label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Mobile:</label>
              <input
                type="text"
                name="mobile"
                value={editFormData.mobile}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Transaction ID:</label>
              <input
                type="text"
                name="transaction_id"
                value={editFormData.transaction_id}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Amount:</label>
              <input
                type="text"
                name="amount"
                value={editFormData.amount}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Referred By:</label>
              <input
                type="text"
                name="referred_by"
                value={editFormData.referred_by}
                onChange={handleInputChange}
                className="border border-gray-300 p-2 w-full"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveEdit}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditingTransaction(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ViewTransactions;